/* eslint-disable no-nested-ternary */
import { Modal } from '@care/react-component-lib';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  Icon48BrandCalendarOneTimeSchedule,
  Icon48BrandCalendarOneWeek,
  Icon48BrandCalendarOneMonth,
  Icon48BrandSearch,
  Icon24InfoResponseTime,
} from '@care/react-icons';
import { Button, Typography, Box, Theme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import createRelativeImageURL from '@/utils/createRelativeImageURL';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import {
  ScreenName,
  ScreenTemplate,
  ServiceIdForMember,
  FLORAL_WHEN_CARE_MODAL,
  UI_FEATURE_FLAGS,
} from '@/constants';
import { SxClassProps } from '@/types/common';
import SeniorCareConversionBox from './SeniorCareConversionBox';
import ChildCareModal from './ChildCareModal';
import PetCareModal from './PetCareModal';

const getClasses = (isNotPopUpRedesignTest: boolean): SxClassProps => ({
  modalContainerWithFlourish: (theme: Theme) => ({
    '& .MuiDialog-paper': {
      '&::before, &::after': {
        position: 'absolute',
        width: '252px',
        left: '50%',
        marginLeft: '-126px',
        height: '78px',
        [theme.breakpoints.up('sm')]: {
          marginTop: '-125px',
          height: '250px',
          top: '50%',
          width: '85px',
          marginLeft: '0px',
        },
      },
      '&::before': {
        content: `url(${createRelativeImageURL(FLORAL_WHEN_CARE_MODAL.mwTopImageURL)}?im=)`,
        top: '-78px',
        [theme.breakpoints.up('sm')]: {
          content: `url(${createRelativeImageURL(FLORAL_WHEN_CARE_MODAL.dtLeftImageURL)}?im=)`,
          left: '-85px',
          top: '50%',
        },
      },
      '&::after': {
        content: `url(${createRelativeImageURL(FLORAL_WHEN_CARE_MODAL.mwBottomImageURL)}?im=)`,
        bottom: '-78px',
        [theme.breakpoints.up('sm')]: {
          content: `url(${createRelativeImageURL(FLORAL_WHEN_CARE_MODAL.dtRightImageURL)}?im=)`,
          right: '-85px',
          left: 'auto',
        },
      },
    },
  }),
  modalContent: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: '295px',
    height: '72px',
    paddingX: 2,
    paddingY: 2,
    border: `1px solid ${theme.palette.care.grey[300]}`,
    boxSizing: 'border-box',
    borderRadius: '16px',
    cursor: 'pointer',
    marginBottom: 2,
    textDecoration: 'none',
  }),
  modalIcon: {
    height: isNotPopUpRedesignTest ? '31.5px' : '40px',
    width: isNotPopUpRedesignTest ? '34.5px' : '40px',
  },
});

interface ModalProp {
  enrollmentURL: string;
  modalTitle: string;
  serviceIdForMember?: string;
  pageType?: string;
}

const WhenCareModal = ({ enrollmentURL, modalTitle, pageType, serviceIdForMember }: ModalProp) => {
  const [open, setOpen] = useState(false);
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    const showModal = true;

    const doesCookieExist = (cookieName: string): boolean =>
      !document.cookie.split(';').some((item: string) => {
        return item.trim().indexOf(cookieName) === 0;
      });

    if (showModal && doesCookieExist('crcmWhenCareModalShown=')) {
      const cookieDate = new Date();
      cookieDate.setDate(cookieDate.getDate() + 90); // 90 days from today

      setTimeout(() => {
        setOpen(true);

        document.cookie = `crcmWhenCareModalShown=true; expires=${cookieDate}`;
      }, 3000);
    }
  }, []); // Only run the useEffect on the initial render

  const isNotPopUpRedesignTest = !(
    serviceIdForMember === ServiceIdForMember.BABYSITTER ||
    serviceIdForMember === ServiceIdForMember.NANNY ||
    serviceIdForMember === ServiceIdForMember.CHILD_CARE ||
    serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION ||
    serviceIdForMember === ServiceIdForMember.FAMILY_CHILD_CARE ||
    serviceIdForMember === ServiceIdForMember.HOUSEKEEPING ||
    serviceIdForMember === ServiceIdForMember.PET_CARE ||
    serviceIdForMember === ServiceIdForMember.PET_DAY_CARE_LOCATION ||
    serviceIdForMember === ServiceIdForMember.PET_SITTING_WALKING ||
    serviceIdForMember === ServiceIdForMember.SENIOR_CARE ||
    serviceIdForMember === ServiceIdForMember.TUTORING ||
    serviceIdForMember === ServiceIdForMember.TUTORING_AFTER_SCHOOL_CARE
  );

  const aboveTheFoldTestJobsFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_3217_ABOVE_THE_FOLD_CONVERSION_TEST_PROVIDERS.id
  );
  const isAboveTheFoldTestJobs = aboveTheFoldTestJobsFlag?.variationIndex === 2;

  const showPopUpChildCare = serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION;

  const showPopUpPetCare =
    serviceIdForMember === ServiceIdForMember.PET_CARE ||
    serviceIdForMember === ServiceIdForMember.PET_DAY_CARE_LOCATION ||
    serviceIdForMember === ServiceIdForMember.PET_SITTING_WALKING;

  const showSeniorCareConversionBox =
    serviceIdForMember === ServiceIdForMember.SENIOR_CARE ||
    serviceIdForMember === ServiceIdForMember.HOME_CARE_LOCATION;

  const classes = getClasses(isNotPopUpRedesignTest);

  const handleModalButton = (e: React.MouseEvent<Element>) => {
    setOpen(false);
    const amplitudeData = {
      asset_type: 'Lightbox',
      CTA: (e.target as HTMLElement).textContent as string,
      messagingContent: 'Join now',
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
    };

    trackAmplitudeEvent(AmplitudeEvent.monetateAssetClicked, amplitudeData);
  };

  const handleCloseModal = () => {
    setOpen(false);

    trackAmplitudeEvent(AmplitudeEvent.monetateAssetClicked, {
      asset_type: 'Lightbox',
      CTA: 'X',
      messagingContent: 'Join now',
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
    });
  };

  const whenCareModalOptions = [
    {
      text: 'Right now',
      intent: 'rightNow',
      icon: <Icon48BrandCalendarOneTimeSchedule sx={classes.modalIcon} />,
    },
    {
      text: 'Within a week',
      intent: 'withInWeek',
      icon: <Icon48BrandCalendarOneWeek sx={classes.modalIcon} />,
    },
    {
      text: 'Within a month',
      intent: 'withInMonth',
      icon: <Icon48BrandCalendarOneMonth sx={classes.modalIcon} />,
    },
    {
      text: 'Just browsing',
      intent: 'justBrowsing',
      icon: <Icon48BrandSearch sx={classes.modalIcon} />,
    },
  ] as const;

  const filteredWhenCareModalOptions = isAboveTheFoldTestJobs
    ? whenCareModalOptions
        .filter((option) => option.intent !== 'withInWeek')
        .map((option) =>
          option.intent === 'rightNow'
            ? {
                ...option,
                icon: <Icon24InfoResponseTime sx={classes.modalIcon} />,
              }
            : option
        )
    : whenCareModalOptions;

  return (
    <Modal
      open={open}
      variant={isMobileOrSmaller ? 'default' : 'dynamic'}
      hideClose={isNotPopUpRedesignTest}
      onClose={handleCloseModal}
      sx={!isNotPopUpRedesignTest ? classes.modalContainerWithFlourish : undefined}>
      {showSeniorCareConversionBox && (
        <SeniorCareConversionBox headline="Who needs care?" enrollmentURL={enrollmentURL} />
      )}

      {showPopUpChildCare && (
        <ChildCareModal
          enrollmentURL={enrollmentURL}
          pageType={pageType}
          serviceIdForMember={serviceIdForMember}
        />
      )}
      {showPopUpPetCare && <PetCareModal enrollmentURL={enrollmentURL} />}
      {!showSeniorCareConversionBox && !showPopUpChildCare && !showPopUpPetCare && (
        <>
          <Typography
            variant="h3"
            textAlign="center"
            mb={3}
            px={isNotPopUpRedesignTest ? 0 : 2}
            fontSize={isNotPopUpRedesignTest ? 'h3.fontSize' : 'serifHeadline3.fontSize'}
            color="textSecondary">
            {modalTitle}
          </Typography>
          {filteredWhenCareModalOptions.map((option) => (
            <Box
              key={option.intent}
              rel="nofollow"
              component="a"
              href={`${enrollmentURL}&intent=${option.intent}`}
              onClick={handleModalButton}
              sx={classes.modalContent}>
              <Box display="flex" alignItems="center">
                {option.icon}
                <Typography
                  variant="h3"
                  color="primary"
                  ml={2}
                  fontWeight={isNotPopUpRedesignTest ? '700' : '400'}>
                  {option.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </>
      )}
      {isNotPopUpRedesignTest && (
        <Button
          color="secondary"
          variant="contained"
          onClick={handleModalButton}
          sx={{
            display: 'block',
            margin: 'auto',
            marginTop: 4,
          }}>
          Close
        </Button>
      )}
    </Modal>
  );
};

export default WhenCareModal;
